import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  ASSISTANT_MESSAGE_MAX_LENGTH,
  HUMAN_SUPPORT_STATUS_LABEL,
  friendlyAssistantError,
} from '../../../services/assistantCentral';
import {
  ASSISTANT_THEME,
  assistantBubblePaperSx,
  assistantComposerWrapSx,
  assistantInputShellSx,
  assistantInputSx,
  assistantLoadingDotSx,
  assistantLoadingSx,
  assistantMessagesAreaSx,
  assistantSendButtonSx,
} from '../assistantTheme';
import {
  inboxChatAreaSx,
  inboxComposerPaperSx,
  inboxFilePreviewSx,
  inboxMessageBubbleSx,
  inboxMessageInputSx,
  inboxPrimaryIconButtonSx,
  INBOX_THEME,
} from '../../../pages/Inbox/inboxTheme';
import AssistantMarkdown, { HELP_ARTICLE_HREF_PREFIX, isInternalHref } from './AssistantMarkdown';
import AssistantToolCalls from './AssistantToolCalls';
import ActionProposalCard from './ActionProposalCard';
import { getAssistantSuggestions } from './assistantSuggestions';
import type { AssistantChatController, AssistantUiMessage } from './useAssistantChat';
import {
  ASSISTANT_MAX_IMAGES_DEFAULT,
  compressAssistantImage,
  createPendingImage,
  isAssistantImageFile,
  PendingAssistantImage,
  revokePendingImage,
} from '../../../utils/assistantImageUpload';

/** Ícones do chat com a equipe Klinvia (compactos, alinhados ao inbox). */
const HUMAN_TEAM_AVATAR_PX = 20;
const HUMAN_TEAM_ICON_PX = 11;
const HUMAN_TEAM_BANNER_ICON_PX = 14;

interface AssistantChatPanelProps {
  chat: AssistantChatController;
  greeting: string;
  /** Avisos exibidos acima das mensagens (ex.: consultas desligadas). */
  notice?: React.ReactNode;
  onNavigate?: () => void;
  /** Visual alinhado à Central de Mensagens (inbox dinâmico). */
  uiVariant?: 'inbox' | 'classic';
  /** Links `help:slug` abrem o artigo na central de ajuda do widget. */
  onOpenHelpArticle?: (slug: string) => void;
}

function TypingIndicator() {
  return (
    <Box sx={assistantLoadingSx}>
      {[0, 160, 320].map((delay) => (
        <Box key={delay} sx={assistantLoadingDotSx(delay)} />
      ))}
    </Box>
  );
}

const AssistantChatPanel: React.FC<AssistantChatPanelProps> = ({
  chat,
  greeting,
  notice,
  onNavigate,
  uiVariant = 'inbox',
  onOpenHelpArticle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const paletteMode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const useInboxUi = uiVariant === 'inbox';
  const [input, setInput] = useState('');
  const [escalating, setEscalating] = useState(false);
  const [escalateNote, setEscalateNote] = useState('');
  const [escalateBusy, setEscalateBusy] = useState(false);
  const [escalateError, setEscalateError] = useState<string | null>(null);
  const [pendingImages, setPendingImages] = useState<PendingAssistantImage[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [chat.messages, chat.sending]);

  const go = (href: string) => {
    if (href.startsWith(HELP_ARTICLE_HREF_PREFIX)) {
      onOpenHelpArticle?.(href.slice(HELP_ARTICLE_HREF_PREFIX.length));
      return;
    }
    if (isInternalHref(href)) {
      navigate(href);
      onNavigate?.();
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const submit = async (text?: string) => {
    const value = (text ?? input).trim();
    if ((!value && !pendingImages.length) || chat.sending) return;
    const files = pendingImages.map((p) => p.file);
    if (text === undefined) setInput('');
    setPendingImages((prev) => {
      prev.forEach(revokePendingImage);
      return [];
    });
    setAttachError(null);
    await chat.send(value, files);
  };

  const addImages = async (fileList: FileList | File[] | null) => {
    const arr = fileList ? Array.from(fileList as FileList) : [];
    if (!arr.length) return;
    setAttachError(null);
    const max = ASSISTANT_MAX_IMAGES_DEFAULT;
    const slots = max - pendingImages.length;
    if (slots <= 0) {
      setAttachError(`Máximo de ${max} imagens por mensagem.`);
      return;
    }
    const picked = arr.slice(0, slots);
    const next: PendingAssistantImage[] = [];
    for (const raw of picked) {
      if (!isAssistantImageFile(raw)) {
        setAttachError('Use JPEG, PNG, WebP ou GIF.');
        continue;
      }
      try {
        const compressed = await compressAssistantImage(raw);
        next.push(createPendingImage(compressed));
      } catch {
        setAttachError('Não foi possível processar uma das imagens. Tente outra foto ou reduza o tamanho.');
      }
    }
    if (next.length) setPendingImages((prev) => [...prev, ...next]);
  };

  const removePending = (id: string) => {
    setPendingImages((prev) => {
      const item = prev.find((p) => p.id === id);
      if (item) revokePendingImage(item);
      return prev.filter((p) => p.id !== id);
    });
  };

  const doEscalate = async () => {
    setEscalateBusy(true);
    setEscalateError(null);
    try {
      await chat.escalate(escalateNote);
      setEscalating(false);
      setEscalateNote('');
    } catch (e) {
      setEscalateError(friendlyAssistantError(e));
    } finally {
      setEscalateBusy(false);
    }
  };

  const bubbleSx = (isUser: boolean, isError?: boolean, isAssistantAi?: boolean) => {
    if (!useInboxUi) {
      return {
        ...assistantBubblePaperSx(isUser ? 'user' : 'assistant'),
        maxWidth: '100%',
        ...(isError ? { borderColor: '#FCA5A5' } : {}),
      };
    }
    const base = inboxMessageBubbleSx(isUser ? 'outbound' : 'inbound', paletteMode);
    const assistantAiInbound =
      !isUser && isAssistantAi && paletteMode === 'light'
        ? {
            bgcolor: ASSISTANT_THEME.assistantBubbleBg,
            borderColor: ASSISTANT_THEME.assistantBubbleBorder,
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
          }
        : {};
    return {
      ...base,
      ...assistantAiInbound,
      maxWidth: '100%',
      ...(isError ? { borderColor: '#FCA5A5' } : {}),
    };
  };

  const renderMessage = (m: AssistantUiMessage) => {
    const isUser = m.role === 'user';
    if (m.sender === 'system') {
      return (
        <Box key={m.localId} sx={{ display: 'flex', justifyContent: 'center', my: 1, position: 'relative', zIndex: 1 }}>
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: 'text.secondary',
              bgcolor: paletteMode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)',
              px: 1.25,
              py: 0.5,
              borderRadius: 2,
              maxWidth: '88%',
              textAlign: 'center',
              whiteSpace: 'pre-wrap',
            }}
          >
            {m.content}
          </Typography>
        </Box>
      );
    }
    const isAgent = m.sender === 'agent';
    return (
      <Box
        key={m.localId}
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          alignItems: 'flex-end',
          gap: isAgent ? 0.5 : 0,
          mb: { xs: 0.75, sm: 1, md: 1.25 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {isAgent && (
          <Avatar
            sx={{
              width: HUMAN_TEAM_AVATAR_PX,
              height: HUMAN_TEAM_AVATAR_PX,
              flexShrink: 0,
              bgcolor: INBOX_THEME.tealSoft,
              color: INBOX_THEME.tealDark,
            }}
          >
            <SupportAgentIcon sx={{ fontSize: HUMAN_TEAM_ICON_PX }} />
          </Avatar>
        )}
        <Box
          sx={{
            maxWidth: useInboxUi
              ? { xs: isUser ? '84%' : '94%', sm: isUser ? '76%' : '92%', md: isUser ? '72%' : '90%' }
              : { xs: isUser ? '90%' : '98%', sm: isUser ? '86%' : '97%', md: isUser ? '82%' : '96%' },
            minWidth: 0,
          }}
        >
          {isAgent && (
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: INBOX_THEME.tealDark, mb: 0.2, ml: 0.15 }}>
              {m.authorName ? `${m.authorName} · Equipe Klinvia` : 'Equipe Klinvia'}
            </Typography>
          )}
          <Paper elevation={0} sx={bubbleSx(isUser, m.isError, !isUser && !isAgent)}>
            {isUser ? (
              <Box>
                {m.attachments && m.attachments.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: m.content ? 0.75 : 0 }}>
                    {m.attachments.map((a, i) =>
                      a.preview_url ? (
                        <Box
                          key={`${m.localId}-att-${i}`}
                          component="img"
                          src={a.preview_url}
                          alt="Anexo"
                          sx={{
                            maxWidth: 160,
                            maxHeight: 120,
                            borderRadius: 1,
                            objectFit: 'cover',
                            border: useInboxUi ? `1px solid ${INBOX_THEME.bubbleOutboundBorder}` : '1px solid rgba(255,255,255,0.35)',
                          }}
                        />
                      ) : null,
                    )}
                  </Box>
                )}
                {m.content ? (
                  <Typography
                    sx={{
                      fontSize: '0.8125rem',
                      whiteSpace: 'pre-wrap',
                      color: useInboxUi ? INBOX_THEME.bubbleText : ASSISTANT_THEME.userBubbleText,
                    }}
                  >
                    {m.content}
                  </Typography>
                ) : null}
              </Box>
            ) : isAgent ? (
              <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.78rem', md: '0.8125rem' }, whiteSpace: 'pre-wrap', color: INBOX_THEME.bubbleText }}>{m.content}</Typography>
            ) : (
              <AssistantMarkdown
                content={m.content}
                onNavigate={go}
                sx={{ color: useInboxUi ? INBOX_THEME.bubbleText : ASSISTANT_THEME.assistantBubbleText }}
              />
            )}
          </Paper>
          {isUser && m.delivery && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              {m.delivery === 'pending' && (
                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Enviando…</Typography>
              )}
              {m.delivery === 'sent' && (
                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Enviada à equipe</Typography>
              )}
              {m.delivery === 'failed' && (
                <>
                  <Typography sx={{ fontSize: '0.65rem', color: 'error.main' }}>Não enviada</Typography>
                  <Button
                    size="small"
                    onClick={() => void chat.retryHumanMessage(m.localId)}
                    sx={{ textTransform: 'none', fontSize: '0.65rem', minWidth: 0, p: 0, lineHeight: 1.2 }}
                  >
                    Tentar de novo
                  </Button>
                </>
              )}
            </Box>
          )}
          {!isUser && <AssistantToolCalls toolCalls={m.toolCalls} />}
          {!isUser &&
            m.proposals.map((p) => (
              <ActionProposalCard key={p.id} proposal={p} onConfirm={chat.confirmProposal} onCancel={chat.cancelProposal} />
            ))}
          {!isUser && m.links.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
              {m.links.map((l) => (
                <Chip
                  key={l.href}
                  size="small"
                  clickable
                  icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                  label={l.label}
                  onClick={() => go(l.href)}
                  sx={{ fontSize: '0.7rem', bgcolor: ASSISTANT_THEME.tealSoft, color: ASSISTANT_THEME.tealDark }}
                />
              ))}
            </Box>
          )}
          {!isUser && !isAgent && m.id && !m.isError && !chat.humanMode && (
            <Box sx={{ display: 'flex', gap: 0.25, mt: 0.25 }}>
              <Tooltip title="Resolveu">
                <span>
                  <IconButton size="small" disabled={Boolean(m.feedback)} onClick={() => void chat.sendFeedback(m.localId, true).catch(() => undefined)}>
                    {m.feedback === 'up' ? <ThumbUpAltIcon sx={{ fontSize: 15, color: 'success.main' }} /> : <ThumbUpAltOutlinedIcon sx={{ fontSize: 15 }} />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Não resolveu">
                <span>
                  <IconButton size="small" disabled={Boolean(m.feedback)} onClick={() => void chat.sendFeedback(m.localId, false).catch(() => undefined)}>
                    {m.feedback === 'down' ? <ThumbDownAltIcon sx={{ fontSize: 15, color: 'error.main' }} /> : <ThumbDownAltOutlinedIcon sx={{ fontSize: 15 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const suggestions = getAssistantSuggestions(location.pathname);
  const empty = chat.messages.length === 0 && !chat.loadingConversation;

  const messagesScrollSx = useInboxUi
    ? {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto' as const,
        px: 2,
        py: 1.5,
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': {
          background: paletteMode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(15, 23, 42, 0.15)',
          borderRadius: '3px',
        },
      }
    : { ...assistantMessagesAreaSx, flex: 1, minHeight: 0, overflowY: 'auto' as const };

  const chatColumnSx = useInboxUi
    ? { ...inboxChatAreaSx(paletteMode), display: 'flex', flexDirection: 'column' as const, flex: 1, minHeight: 0 }
    : { display: 'flex', flexDirection: 'column' as const, flex: 1, minHeight: 0 };

  return (
    <Box sx={chatColumnSx}>
      {chat.support?.human_mode && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            bgcolor: chat.support.status === 'resolved' ? 'action.hover' : ASSISTANT_THEME.tealSoft,
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <SupportAgentIcon sx={{ fontSize: HUMAN_TEAM_BANNER_ICON_PX, color: INBOX_THEME.tealDark }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: INBOX_THEME.tealDark }} noWrap>
              {chat.support.status === 'resolved' ? 'Atendimento encerrado' : 'Você está falando com a equipe Klinvia'}
            </Typography>
            <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }} noWrap>
              Chamado {chat.support.protocol} · {HUMAN_SUPPORT_STATUS_LABEL[chat.support.status]}
              {chat.supportOffline ? ' · reconectando…' : ''}
            </Typography>
          </Box>
        </Box>
      )}
      <Box sx={messagesScrollSx}>
        {notice}
        {chat.loadError && (
          <Alert severity="warning" sx={{ mb: 1, fontSize: '0.75rem' }}>
            {chat.loadError}
          </Alert>
        )}
        {chat.loadingConversation && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {empty && (
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Paper elevation={0} sx={{ ...bubbleSx(false, false, true), mb: 1.25 }}>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: useInboxUi ? INBOX_THEME.bubbleText : ASSISTANT_THEME.assistantBubbleText,
                }}
              >
                {greeting}
              </Typography>
            </Paper>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
              {suggestions.map((q) => (
                <Chip
                  key={q}
                  label={q}
                  clickable
                  variant="outlined"
                  onClick={() => void submit(q)}
                  sx={{ fontSize: '0.74rem', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                />
              ))}
            </Box>
          </Box>
        )}
        {chat.messages.map(renderMessage)}
        {chat.sending && !chat.humanMode && <TypingIndicator />}
        {chat.escalation && !chat.humanMode && (
          <Alert
            severity="success"
            icon={<SupportAgentIcon sx={{ fontSize: HUMAN_TEAM_BANNER_ICON_PX }} />}
            sx={{
              mt: 1,
              fontSize: '0.75rem',
              position: 'relative',
              zIndex: 1,
              '& .MuiAlert-icon': { py: 0.25, mr: 1, alignItems: 'center' },
            }}
          >
            Protocolo {chat.escalation.protocol}
          </Alert>
        )}
        <div ref={endRef} />
      </Box>

      <Box
        component={useInboxUi ? Paper : Box}
        elevation={0}
        sx={useInboxUi ? inboxComposerPaperSx : assistantComposerWrapSx}
      >
        {chat.canEscalate && !escalating && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.75 }}>
            <Button
              size="small"
              startIcon={<SupportAgentIcon sx={{ fontSize: HUMAN_TEAM_ICON_PX }} />}
              onClick={() => setEscalating(true)}
              sx={{ textTransform: 'none', fontSize: '0.72rem' }}
            >
              Falar com um humano
            </Button>
          </Box>
        )}
        {escalating && (
          <Box sx={{ mb: 1 }}>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder="Quer acrescentar algo para a equipe de suporte? (opcional)"
              value={escalateNote}
              onChange={(e) => setEscalateNote(e.target.value.slice(0, 1000))}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem' } }}
            />
            {escalateError && (
              <Alert severity="error" sx={{ mt: 0.5, py: 0, fontSize: '0.72rem' }}>
                {escalateError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
              <Button size="small" onClick={() => setEscalating(false)} sx={{ textTransform: 'none' }}>
                Voltar
              </Button>
              <Button size="small" variant="contained" disabled={escalateBusy} onClick={() => void doEscalate()} sx={{ textTransform: 'none' }}>
                {escalateBusy ? <CircularProgress size={14} color="inherit" /> : 'Abrir chamado'}
              </Button>
            </Box>
          </Box>
        )}
        {attachError && (
          <Alert severity="warning" sx={{ mb: 0.5, py: 0, fontSize: '0.72rem' }}>
            {attachError}
          </Alert>
        )}
        {pendingImages.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
            {pendingImages.map((p) => (
              <Box key={p.id} sx={{ position: 'relative', ...(useInboxUi ? inboxFilePreviewSx : {}) }}>
                <Box
                  component="img"
                  src={p.previewUrl}
                  alt="Pré-visualização"
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: useInboxUi ? 'none' : `1px solid ${ASSISTANT_THEME.tealSoft}`,
                  }}
                />
                <IconButton
                  size="small"
                  aria-label="Remover imagem"
                  onClick={() => removePending(p.id)}
                  sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'background.paper', boxShadow: 1, width: 22, height: 22 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        {useInboxUi ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              hidden
              onChange={(e) => {
                void addImages(e.target.files);
                e.target.value = '';
              }}
            />
            <Tooltip title="Anexar captura de tela ou foto">
              <span>
                <IconButton
                  aria-label="Anexar imagem"
                  disabled={chat.sending || pendingImages.length >= ASSISTANT_MAX_IMAGES_DEFAULT}
                  onClick={() => fileInputRef.current?.click()}
                  sx={inboxPrimaryIconButtonSx}
                >
                  <ImageOutlinedIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </span>
            </Tooltip>
            <TextField
              fullWidth
              multiline
              maxRows={5}
              size="small"
              placeholder={chat.humanMode ? 'Escreva para a equipe Klinvia ou envie uma captura…' : 'Descreva sua dúvida, cole o erro ou envie uma captura…'}
              value={input}
              disabled={chat.sending}
              onChange={(e) => setInput(e.target.value.slice(0, ASSISTANT_MESSAGE_MAX_LENGTH))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void submit();
                }
              }}
              onPaste={(e) => {
                const items = e.clipboardData?.items;
                if (!items?.length) return;
                const files: File[] = [];
                for (let i = 0; i < items.length; i += 1) {
                  const item = items[i];
                  if (item.kind === 'file' && item.type.startsWith('image/')) {
                    const f = item.getAsFile();
                    if (f) files.push(f);
                  }
                }
                if (files.length) {
                  e.preventDefault();
                  void addImages(files);
                }
              }}
              sx={inboxMessageInputSx(paletteMode)}
            />
            <IconButton
              aria-label="Enviar"
              onClick={() => void submit()}
              disabled={chat.sending || (!input.trim() && !pendingImages.length)}
              sx={{
                ...inboxPrimaryIconButtonSx,
                bgcolor: INBOX_THEME.teal,
                color: '#fff',
                '&:hover': { bgcolor: INBOX_THEME.tealDark, color: '#fff' },
                '&.Mui-disabled': { bgcolor: INBOX_THEME.slateFaint, color: '#fff' },
              }}
            >
              <SendIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        ) : (
          <Box sx={assistantInputShellSx}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              hidden
              onChange={(e) => {
                void addImages(e.target.files);
                e.target.value = '';
              }}
            />
            <Tooltip title="Anexar captura de tela ou foto">
              <span>
                <IconButton
                  aria-label="Anexar imagem"
                  disabled={chat.sending || pendingImages.length >= ASSISTANT_MAX_IMAGES_DEFAULT}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ color: ASSISTANT_THEME.tealDark, mr: 0.25 }}
                >
                  <ImageOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            <InputBase
              fullWidth
              multiline
              maxRows={5}
              placeholder={chat.humanMode ? 'Escreva para a equipe Klinvia ou envie uma captura…' : 'Descreva sua dúvida, cole o erro ou envie uma captura de tela…'}
              value={input}
              disabled={chat.sending}
              onChange={(e) => setInput(e.target.value.slice(0, ASSISTANT_MESSAGE_MAX_LENGTH))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void submit();
                }
              }}
              onPaste={(e) => {
                const items = e.clipboardData?.items;
                if (!items?.length) return;
                const files: File[] = [];
                for (let i = 0; i < items.length; i += 1) {
                  const item = items[i];
                  if (item.kind === 'file' && item.type.startsWith('image/')) {
                    const f = item.getAsFile();
                    if (f) files.push(f);
                  }
                }
                if (files.length) {
                  e.preventDefault();
                  void addImages(files);
                }
              }}
              sx={assistantInputSx}
            />
            <IconButton
              aria-label="Enviar"
              onClick={() => void submit()}
              disabled={chat.sending || (!input.trim() && !pendingImages.length)}
              sx={assistantSendButtonSx}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AssistantChatPanel;
